import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'

export default function AdminSettings() {
  const { user } = useAuth()
  const [tab, setTab] = useState('tasks')
  const [tasks, setTasks] = useState([])
  const [profiles, setProfiles] = useState([])
  const [assignments, setAssignments] = useState([])
  const [settings, setSettings] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)
  const [editTask, setEditTask] = useState(null)

  useEffect(() => { loadAll() }, [])

  const loadAll = async () => {
    setLoading(true)
    const [taskRes, profileRes, assignRes, settingsRes] = await Promise.all([
      supabase.from('task_definitions').select('*').order('frequency').order('due_time'),
      supabase.from('profiles').select('*').eq('is_active', true).order('full_name'),
      supabase.from('task_assignments').select('*, profiles!staff_id(full_name), manager:profiles!line_manager_id(full_name), task_definitions(name)'),
      supabase.from('app_settings').select('*'),
    ])
    setTasks(taskRes.data || [])
    setProfiles(profileRes.data || [])
    setAssignments(assignRes.data || [])
    const s = {}
    ;(settingsRes.data || []).forEach(r => { s[r.key] = r.value })
    setSettings(s)
    setLoading(false)
  }

  const handleSaveTask = async () => {
    setSaving(true)
    const { id, ...data } = editTask
    const { error } = id
      ? await supabase.from('task_definitions').update({ ...data, updated_at:new Date().toISOString() }).eq('id', id)
      : await supabase.from('task_definitions').insert({ ...data, created_by:user.id })
    if (error) { setMsg({ type:'error', text:error.message }) }
    else { setMsg({ type:'success', text:'Task saved.' }); setEditTask(null); loadAll() }
    setSaving(false); setTimeout(() => setMsg(null), 3000)
  }

  const handleAssign = async (taskId, staffId, managerId) => {
    const existing = assignments.find(a => a.task_id===taskId && a.staff_id===staffId)
    if (existing) {
      await supabase.from('task_assignments').update({ line_manager_id:managerId||null }).eq('id', existing.id)
    } else {
      await supabase.from('task_assignments').insert({ task_id:taskId, staff_id:staffId, line_manager_id:managerId||null, assigned_by:user.id })
    }
    loadAll()
  }

  const handleUnassign = async (taskId, staffId) => {
    await supabase.from('task_assignments').delete().eq('task_id', taskId).eq('staff_id', staffId)
    loadAll()
  }

  const handleSaveSetting = async (key, value) => {
    await supabase.from('app_settings').upsert({ key, value, updated_by:user.id, updated_at:new Date().toISOString() }, { onConflict:'key' })
    setMsg({ type:'success', text:'Setting saved.' })
    setTimeout(() => setMsg(null), 2000)
  }

  const handleToggleDigital = async (profileId, currentValue) => {
    await supabase.from('profiles').update({ is_digital:!currentValue }).eq('id', profileId)
    loadAll()
  }

  const handleSetLineManager = async (staffId, managerId) => {
    await supabase.from('profiles').update({ line_manager_id:managerId||null }).eq('id', staffId)
    loadAll()
  }

  const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday']
  const ROLES = ['operations','finance','stores','hr','sales','driver']
  const SECTIONS = ['farm-log','harvest','quality','logistics','cash-log','invoices','weekly-pl','procurement','inventory','timesheets','visitors','incidents','sops','emergency']

  const emptyTask = { name:'', description:'', section:'', assigned_role:'operations', frequency:'daily', due_time:'16:30', due_day:'Monday', notify_upcoming:60, notify_overdue:30, allows_manual:true, is_active:true }

  return (
    <AppLayout title="Admin Settings">
      <div className="section-header">
        <div className="section-icon">⚙️</div>
        <div className="section-info">
          <h2>Admin Settings</h2>
          <p>Configure tasks, notifications, crop targets, and staff assignments.</p>
        </div>
      </div>

      {msg && <div className={`alert ${msg.type==='success'?'alert-green':'alert-red'}`}>{msg.text}</div>}

      <div className="tabs">
        <button className={`tab ${tab==='tasks'?'active':''}`} onClick={() => setTab('tasks')}>📋 Task Definitions</button>
        <button className={`tab ${tab==='assignments'?'active':''}`} onClick={() => setTab('assignments')}>👤 Staff Assignments</button>
        <button className={`tab ${tab==='owners'?'active':''}`} onClick={() => setTab('owners')}>🏷️ Module Owners</button>
        <button className={`tab ${tab==='notifications'?'active':''}`} onClick={() => setTab('notifications')}>🔔 Notifications</button>
        <button className={`tab ${tab==='staff'?'active':''}`} onClick={() => setTab('staff')}>👥 Staff Settings</button>
        <button className={`tab ${tab==='general'?'active':''}`} onClick={() => setTab('general')}>⚙️ General</button>
      </div>

      {tab === 'tasks' && (
        <div>
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:12 }}>
            <button className="btn btn-primary" onClick={() => setEditTask({ ...emptyTask })}>+ New Task</button>
          </div>

          {editTask && (
            <div className="card" style={{ marginBottom:16, border:'2px solid var(--amber)' }}>
              <div className="card-title">{editTask.id ? 'Edit Task' : 'New Task Definition'}</div>
              <div className="grid-2">
                <div className="form-group"><label className="form-label">Task Name *</label><input className="form-input" value={editTask.name} onChange={e => setEditTask(t=>({...t,name:e.target.value}))} /></div>
                <div className="form-group"><label className="form-label">Assigned Role</label>
                  <select className="form-select" value={editTask.assigned_role} onChange={e => setEditTask(t=>({...t,assigned_role:e.target.value}))}>
                    {ROLES.map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div className="form-group"><label className="form-label">Description</label><input className="form-input" value={editTask.description||''} onChange={e => setEditTask(t=>({...t,description:e.target.value}))} /></div>
                <div className="form-group"><label className="form-label">Links to Section</label>
                  <select className="form-select" value={editTask.section||''} onChange={e => setEditTask(t=>({...t,section:e.target.value}))}>
                    <option value="">None</option>
                    {SECTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group"><label className="form-label">Frequency</label>
                  <select className="form-select" value={editTask.frequency} onChange={e => setEditTask(t=>({...t,frequency:e.target.value}))}>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                {editTask.frequency==='weekly' && (
                  <div className="form-group"><label className="form-label">Due Day</label>
                    <select className="form-select" value={editTask.due_day||'Monday'} onChange={e => setEditTask(t=>({...t,due_day:e.target.value}))}>
                      {DAYS.map(d => <option key={d}>{d}</option>)}
                    </select>
                  </div>
                )}
                <div className="form-group"><label className="form-label">Due Time</label><input className="form-input" type="time" value={editTask.due_time||'16:30'} onChange={e => setEditTask(t=>({...t,due_time:e.target.value}))} /></div>
                <div className="form-group"><label className="form-label">Notify Upcoming (mins before)</label><input className="form-input" type="number" value={editTask.notify_upcoming} onChange={e => setEditTask(t=>({...t,notify_upcoming:parseInt(e.target.value)}))} /></div>
                <div className="form-group"><label className="form-label">Notify Overdue (mins after)</label><input className="form-input" type="number" value={editTask.notify_overdue} onChange={e => setEditTask(t=>({...t,notify_overdue:parseInt(e.target.value)}))} /></div>
              </div>
              <div style={{ display:'flex', gap:10, marginTop:8 }}>
                <button className="btn btn-primary" onClick={handleSaveTask} disabled={saving}>{saving?<><span className="spinner" />Saving...</>:'💾 Save Task'}</button>
                <button className="btn btn-ghost" onClick={() => setEditTask(null)}>Cancel</button>
              </div>
            </div>
          )}

          {loading ? <div style={{ textAlign:'center', padding:32 }}><span className="spinner dark" /></div>
          : (
            <div className="card">
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Task Name</th><th>Role</th><th>Frequency</th><th>Due Time</th><th>Notify (min)</th><th>Manual OK?</th><th>Active</th><th>Actions</th></tr></thead>
                  <tbody>
                    {tasks.map(task => (
                      <tr key={task.id}>
                        <td>
                          <div style={{ fontWeight:600 }}>{task.name}</div>
                          {task.description && <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{task.description}</div>}
                        </td>
                        <td><span className="badge badge-grey">{task.assigned_role}</span></td>
                        <td style={{ fontSize:'0.78rem' }}>{task.frequency}{task.due_day?` — ${task.due_day}`:''}</td>
                        <td style={{ fontFamily:"'DM Mono',monospace", fontWeight:600 }}>{task.due_time?.slice(0,5)}</td>
                        <td style={{ fontSize:'0.78rem' }}>↑{task.notify_upcoming}m / ↓{task.notify_overdue}m</td>
                        <td>{task.allows_manual?'✅':'❌'}</td>
                        <td>{task.is_active?<span className="badge badge-green">Active</span>:<span className="badge badge-red">Off</span>}</td>
                        <td>
                          <div style={{ display:'flex', gap:5 }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => setEditTask(task)}>Edit</button>
                            <button className="btn btn-danger btn-sm" onClick={async () => { await supabase.from('task_definitions').update({ is_active:!task.is_active }).eq('id',task.id); loadAll() }}>
                              {task.is_active?'Disable':'Enable'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'assignments' && (
        <div className="card">
          <div className="card-title">Assign Tasks to Staff</div>
          <div className="card-subtitle">Select which staff member is responsible for each task, and who their line manager is.</div>
          {loading ? <div style={{ textAlign:'center', padding:32 }}><span className="spinner dark" /></div>
          : tasks.map(task => {
            const taskAssignments = assignments.filter(a => a.task_id === task.id)
            return (
              <div key={task.id} style={{ marginBottom:20, paddingBottom:20, borderBottom:'1px solid var(--border)' }}>
                <div style={{ fontWeight:700, marginBottom:8, display:'flex', alignItems:'center', gap:8 }}>
                  <span>{task.name}</span>
                  <span className="badge badge-grey">{task.frequency}</span>
                  <span style={{ fontFamily:"'DM Mono',monospace", fontSize:'0.72rem', color:'var(--amber)' }}>Due {task.due_time?.slice(0,5)}</span>
                </div>
                {taskAssignments.length > 0 && (
                  <div style={{ marginBottom:8 }}>
                    {taskAssignments.map(a => (
                      <div key={a.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 10px', background:'var(--green-pale)', borderRadius:6, marginBottom:4, fontSize:'0.82rem' }}>
                        <span style={{ fontWeight:600 }}>👤 {a.profiles?.full_name}</span>
                        {a.manager && <span style={{ color:'var(--text-muted)' }}>→ Line manager: {a.manager.full_name}</span>}
                        <button className="btn btn-danger btn-sm" style={{ marginLeft:'auto' }} onClick={() => handleUnassign(task.id, a.staff_id)}>Remove</button>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <select className="form-select" style={{ maxWidth:200 }} id={`staff-${task.id}`}>
                    <option value="">Assign staff member...</option>
                    {profiles.filter(p => !taskAssignments.find(a => a.staff_id===p.id)).map(p => (
                      <option key={p.id} value={p.id}>{p.full_name || p.email}</option>
                    ))}
                  </select>
                  <select className="form-select" style={{ maxWidth:200 }} id={`manager-${task.id}`}>
                    <option value="">Line manager (optional)</option>
                    {profiles.filter(p => ['admin','manager'].includes(p.permission_level)).map(p => (
                      <option key={p.id} value={p.id}>{p.full_name || p.email}</option>
                    ))}
                  </select>
                  <button className="btn btn-secondary btn-sm" onClick={() => {
                    const staffEl = document.getElementById(`staff-${task.id}`)
                    const mgrEl = document.getElementById(`manager-${task.id}`)
                    if (staffEl?.value) handleAssign(task.id, staffEl.value, mgrEl?.value||null)
                  }}>+ Assign</button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {tab === 'notifications' && <NotificationsTab />}

      {tab === 'staff' && (
        <div className="card">
          <div className="card-title">Staff Settings</div>
          <div className="card-subtitle">Set digital/manual status and line managers for each staff member.</div>
          <div className="alert alert-amber">
            <strong>Digital staff</strong> receive email notifications. <strong>Manual staff</strong> don't have digital access — their tasks appear on their line manager's dashboard instead.
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Staff Member</th><th>Role</th><th>Digital Access?</th><th>Line Manager</th><th>Phone</th></tr></thead>
              <tbody>
                {profiles.map(p => (
                  <tr key={p.id}>
                    <td style={{ fontWeight:600 }}>{p.full_name || p.email}</td>
                    <td><span className={`badge ${p.permission_level==='admin'?'badge-admin':p.permission_level==='manager'?'badge-green':'badge-grey'}`}>{p.permission_level}</span></td>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }} onClick={() => handleToggleDigital(p.id, p.is_digital!==false)}>
                        <span style={{ fontSize:'1.1rem' }}>{p.is_digital!==false?'✅':'⬜'}</span>
                        <span style={{ fontSize:'0.78rem' }}>{p.is_digital!==false?'Digital':'Manual'}</span>
                      </div>
                    </td>
                    <td>
                      <select className="form-select" style={{ maxWidth:180 }} value={p.line_manager_id||''} onChange={e => handleSetLineManager(p.id, e.target.value||null)}>
                        <option value="">No line manager</option>
                        {profiles.filter(m => m.id!==p.id && ['admin','manager'].includes(m.permission_level)).map(m => (
                          <option key={m.id} value={m.id}>{m.full_name||m.email}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ fontFamily:"'DM Mono',monospace", fontSize:'0.78rem' }}>{p.phone||'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'general' && (
        <div>
          {/* Crop Targets */}
          <div className="card" style={{ marginBottom:16 }}>
            <div className="card-title">🌱 Crop Target Ranges</div>
            <div className="card-subtitle">These targets are shown in the farm log to guide Sharon's readings. Edit per crop.</div>
            {settings.crop_targets && Object.entries(settings.crop_targets).map(([crop, targets]) => (
              <div key={crop} style={{ marginBottom:20, paddingBottom:20, borderBottom:'1px solid var(--border)' }}>
                <div style={{ fontWeight:700, marginBottom:10 }}>{crop}</div>
                <div className="grid-3">
                  {[['ph_min','pH Min'],['ph_max','pH Max'],['ec_min','EC Min'],['ec_max','EC Max'],['water_temp_min','Water Temp Min (°C)'],['water_temp_max','Water Temp Max (°C)']].map(([key,label]) => (
                    <div className="form-group" key={key}>
                      <label className="form-label">{label}</label>
                      <input className="form-input" type="number" step="0.1"
                        value={settings.crop_targets[crop][key]}
                        onChange={e => {
                          const updated = { ...settings.crop_targets, [crop]: { ...settings.crop_targets[crop], [key]: parseFloat(e.target.value) } }
                          setSettings(s => ({ ...s, crop_targets: updated }))
                        }}
                      />
                    </div>
                  ))}
                </div>
                <button className="btn btn-secondary btn-sm" onClick={() => handleSaveSetting('crop_targets', settings.crop_targets)}>💾 Save {crop} Targets</button>
              </div>
            ))}
          </div>

          {/* Payment thresholds */}
          <div className="card" style={{ marginBottom:16 }}>
            <div className="card-title">💰 Payment Approval Thresholds</div>
            <div className="card-subtitle">Payments below Sharon's limit can be approved by Sharon alone. Above requires both.</div>
            {settings.payment_thresholds && (
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Sharon's Approval Limit (UGX)</label>
                  <input className="form-input" type="number" value={settings.payment_thresholds.sharon_limit}
                    onChange={e => setSettings(s => ({ ...s, payment_thresholds: { ...s.payment_thresholds, sharon_limit: parseInt(e.target.value), require_dual_approval_above: parseInt(e.target.value) } }))}
                  />
                </div>
              </div>
            )}
            <button className="btn btn-secondary btn-sm" style={{ marginTop:8 }} onClick={() => handleSaveSetting('payment_thresholds', settings.payment_thresholds)}>💾 Save</button>
          </div>

          {/* Working hours */}
          <div className="card">
            <div className="card-title">🕐 Working Hours</div>
            {settings.working_hours && (
              <div className="grid-2">
                <div className="form-group"><label className="form-label">Start Time</label><input className="form-input" type="time" value={settings.working_hours.start} onChange={e => setSettings(s => ({ ...s, working_hours: { ...s.working_hours, start:e.target.value } }))} /></div>
                <div className="form-group"><label className="form-label">End Time</label><input className="form-input" type="time" value={settings.working_hours.end} onChange={e => setSettings(s => ({ ...s, working_hours: { ...s.working_hours, end:e.target.value } }))} /></div>
              </div>
            )}
            <button className="btn btn-secondary btn-sm" onClick={() => handleSaveSetting('working_hours', settings.working_hours)}>💾 Save</button>
          </div>
        </div>
      )}

      {tab === 'owners' && (
        <ModuleOwnersTab profiles={profiles} />
      )}
    </AppLayout>
  )
}

// ─── MODULE OWNERS TAB ──────────────────────────────────────────────────────
const MODULES = [
  { id:'farm_log', label:'Crop Farm Log', icon:'🌱', freq:'Daily' },
  { id:'cash_log', label:'Daily Cash Log', icon:'💵', freq:'Daily' },
  { id:'harvest_log', label:'Harvest Log', icon:'🌾', freq:'As needed' },
  { id:'quality', label:'Quality Control', icon:'✅', freq:'Daily' },
  { id:'weekly_pl', label:'Weekly P&L', icon:'📊', freq:'Weekly (Friday)' },
  { id:'timesheets', label:'Timesheets', icon:'⏱️', freq:'Weekly' },
  { id:'requisitions', label:'Requisitions', icon:'📋', freq:'As needed' },
  { id:'procurement', label:'Purchase Orders', icon:'📦', freq:'As needed' },
  { id:'goods_received', label:'Goods Received', icon:'📥', freq:'As needed' },
  { id:'payables', label:'Payables', icon:'💸', freq:'Weekly' },
  { id:'invoices', label:'Invoices', icon:'📄', freq:'As needed' },
  { id:'customers', label:'Customers', icon:'🤝', freq:'As needed' },
  { id:'logistics', label:'Logistics & Deliveries', icon:'🚚', freq:'As needed' },
  { id:'inventory', label:'Inventory & Stores', icon:'🏪', freq:'Weekly audit' },
  { id:'staff_register', label:'Staff Register', icon:'👥', freq:'Quarterly audit' },
  { id:'visitors', label:'Visitor Log', icon:'👤', freq:'Ad hoc' },
  { id:'incidents', label:'Incident Reports', icon:'🚨', freq:'Ad hoc' },
  { id:'financial_statements', label:'Financial Statements', icon:'📊', freq:'Monthly' },
  { id:'fixed_assets', label:'Fixed Assets', icon:'🏗️', freq:'Quarterly audit' },
]

function ModuleOwnersTab({ profiles }) {
  const [owners, setOwners] = React.useState({})
  const [saving, setSaving] = React.useState(false)
  const [msg, setMsg] = React.useState(null)

  React.useEffect(() => {
    supabase.from('app_settings').select('key,value').like('key','module_owner_%')
      .then(({ data }) => {
        const o = {}
        ;(data||[]).forEach(s => { o[s.key.replace('module_owner_','')] = s.value })
        setOwners(o)
      })
  }, [])

  const handleSaveAll = async () => {
    setSaving(true); setMsg(null)
    for (const [moduleId, ownerName] of Object.entries(owners)) {
      const key = `module_owner_${moduleId}`
      const { data: existing } = await supabase.from('app_settings').select('id').eq('key', key).single()
      if (existing) {
        await supabase.from('app_settings').update({ value: ownerName }).eq('key', key)
      } else {
        await supabase.from('app_settings').insert({ key, value: ownerName })
      }
    }
    setMsg({ type:'success', text:'Module owners saved. They now show on the Dashboard.' })
    setSaving(false); setTimeout(() => setMsg(null), 3000)
  }

  return (
    <div className="card">
      <div className="card-title">🏷️ Module Owners</div>
      <div className="card-subtitle">Assign a responsible person to each module. They will be shown on the Dashboard and held accountable for keeping that area up to date.</div>

      {msg && <div className={`alert ${msg.type==='success'?'alert-green':'alert-red'}`}>{msg.text}</div>}

      <div className="alert alert-amber" style={{ marginBottom:16 }}>
        💡 Assigning an owner means that person is <strong>accountable</strong> for that module being kept up to date. It shows on the dashboard and in audit reports. It does NOT restrict other staff from using the module.
      </div>

      <div className="table-wrap" style={{ marginBottom:16 }}>
        <table>
          <thead><tr><th>Module</th><th>Frequency</th><th>Responsible Person</th></tr></thead>
          <tbody>
            {MODULES.map(m => (
              <tr key={m.id}>
                <td style={{ fontWeight:600 }}>{m.icon} {m.label}</td>
                <td><span className="badge badge-grey" style={{ fontSize:'0.68rem' }}>{m.freq}</span></td>
                <td>
                  <select className="form-select" style={{ maxWidth:220 }} value={owners[m.id]||''} onChange={e => setOwners(o => ({ ...o, [m.id]: e.target.value }))}>
                    <option value="">— Unassigned —</option>
                    {profiles.map(p => <option key={p.id} value={p.full_name||p.email}>{p.full_name||p.email}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button className="btn btn-primary btn-lg" onClick={handleSaveAll} disabled={saving}>
        {saving ? <><span className="spinner"/>Saving...</> : '💾 Save All Module Owners'}
      </button>
    </div>
  )
}

// ─── NOTIFICATIONS TAB ──────────────────────────────────────────────────────
function NotificationsTab() {
  const [sending, setSending] = React.useState(false)
  const [result, setResult] = React.useState(null)

  const handleSendNow = async () => {
    setSending(true); setResult(null)
    try {
      const r = await fetch('/api/send-reminders', { method: 'POST' })
      const d = await r.json()
      setResult({ type: 'success', text: `Sent ${d.emailsSent} reminder email(s). ${d.errors > 0 ? d.errors + ' failed.' : ''}` })
    } catch (e) {
      setResult({ type: 'error', text: 'Failed to send: ' + e.message })
    }
    setSending(false)
  }

  return (
    <div>
      {result && <div className={`alert ${result.type === 'success' ? 'alert-green' : 'alert-red'}`}>{result.text}</div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">🔔 Email Reminders</div>
        <div className="card-subtitle">Automated reminders sent to assigned staff via email</div>

        <div className="table-wrap" style={{ marginBottom: 16 }}>
          <table>
            <thead><tr><th>Time</th><th>Days</th><th>Reminder</th><th>Recipients</th></tr></thead>
            <tbody>
              {[
                ['7:00 AM', 'Mon–Sat', 'Morning task reminder — pending daily tasks', 'Assigned staff'],
                ['4:00 PM', 'Mon–Sat', 'Afternoon alert — critical tasks not yet done (farm log, cash log)', 'Assigned staff'],
                ['2:00 PM', 'Friday only', 'Weekly P&L due reminder', 'Admins & Managers'],
                ['8:00 AM', 'Monday only', 'New week checklist — review P&L, check stock, follow up invoices', 'Admins & Managers'],
              ].map(([time, days, reminder, recipients]) => (
                <tr key={time}>
                  <td style={{ fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>{time}</td>
                  <td style={{ fontSize: '0.78rem' }}>{days}</td>
                  <td style={{ fontSize: '0.82rem' }}>{reminder}</td>
                  <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{recipients}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="alert alert-amber" style={{ marginBottom: 14 }}>
          ℹ️ Reminders run automatically via Vercel cron jobs. Staff must have email addresses set in their profile to receive reminders. Manual workers don't receive emails — their line manager gets a note to collect their paper forms by midday.
        </div>

        <button className="btn btn-primary" onClick={handleSendNow} disabled={sending}>
          {sending ? <><span className="spinner" />Sending...</> : '📧 Send Reminders Now (Test)'}
        </button>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">📱 WhatsApp Business Notifications</div>
        <div className="card-subtitle">Not yet configured — follow steps below to set up</div>

        <div className="alert alert-red" style={{ marginBottom: 14 }}>
          ⚠️ <strong>Action Required:</strong> Register with Meta to enable WhatsApp notifications. This will allow reminders to be sent via WhatsApp in addition to email — much more effective for field staff.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[
            ['1', 'Create Meta Business Account', 'Go to business.facebook.com → Create Account → Verify your business', 'https://business.facebook.com/'],
            ['2', 'Apply for WhatsApp Business API', 'In Meta Business Suite → WhatsApp → Get Started → Apply for API access', 'https://business.facebook.com/wa/manage/'],
            ['3', 'Verify Phone Number', 'Add and verify a dedicated WhatsApp Business number (can be a new SIM or existing number)', null],
            ['4', 'Get API Credentials', 'Copy your Phone Number ID and Access Token from Meta Developer Console', 'https://developers.facebook.com/'],
            ['5', 'Add to Vercel Environment', 'Add WHATSAPP_PHONE_ID and WHATSAPP_TOKEN to Vercel Environment Variables', null],
          ].map(([num, title, desc, link]) => (
            <div key={num} style={{ display: 'flex', gap: 14, padding: 14, background: 'var(--cream)', borderRadius: 8 }}>
              <div style={{ width: 28, height: 28, minWidth: 28, background: 'var(--green-deep)', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.8rem' }}>{num}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 3 }}>{title}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: link ? 6 : 0 }}>{desc}</div>
                {link && <a href={link} target="_blank" rel="noreferrer" style={{ fontSize: '0.75rem', color: 'var(--blue)', fontWeight: 700 }}>Open → {link}</a>}
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 14, padding: 14, background: 'var(--amber-pale)', borderRadius: 8, border: '1px solid var(--amber-light)' }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>📅 Estimated Timeline</div>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', lineHeight: 1.7 }}>
            Account setup: ~1 hour · Meta review and approval: 24–48 hours · Technical integration: ~2 hours<br/>
            <strong>Total: 2–3 days from starting</strong>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-title">📋 Manual Worker Reminder Flow</div>
        <div className="card-subtitle">How reminders work for staff without system access</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          {[
            ['7:00 AM', 'Manager receives email', 'Lists all tasks for the day including manual workers under their supervision'],
            ['12:00 PM', 'Paper form deadline', 'Manual workers must hand in paper forms to their manager by midday'],
            ['1:00 PM', 'Manager enters data', 'Manager logs in and enters manual workers\' data on their behalf before end of day'],
          ].map(([time, title, desc]) => (
            <div key={time} style={{ background: 'var(--cream)', borderRadius: 8, padding: 14 }}>
              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.75rem', color: 'var(--amber)', fontWeight: 700, marginBottom: 4 }}>{time}</div>
              <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 4 }}>{title}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
